import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { AuthenticationService, BaseApiService, EnvironmentConfig } from "../../../core/core";
import { RentalRequest } from "../models/rental-request.model";
import { CartItem } from "../../../cart/cart";
import { CurrencyPipe } from "@angular/common";
import { Observable, forkJoin, map, mergeMap, of } from "rxjs";
import { Operation } from "fast-json-patch";
import { AzureSearchRequest, AzureSearchService } from "../../../azure-search/azure-search";
import { TelematicsService } from "../../services/telematics.service";
import { RentalRequestItem } from "../models/rental-request-item.model";

declare var environment: EnvironmentConfig;

@Injectable({
  providedIn: "root",
})
export class RentalRequestService extends BaseApiService<RentalRequest> {
  constructor(
    httpClient: HttpClient,
    authenticationService: AuthenticationService,
    private currencyPipe: CurrencyPipe,
    private azureSearchService: AzureSearchService,
    private telematicsService: TelematicsService,
  ) {
    super(httpClient, environment.ordersApiDomain, "/api/orders/v2/order", true, authenticationService);
  }

  addRentalItem(rentalRequestId: string, cartItem: CartItem, rentalItem: RentalRequestItem) {
    const headers = this.getAuth();
    return this.httpClient.put<any>(`${environment.ordersApiDomain}/api/orders/v2/order/${rentalRequestId}/additem`, { cartItem, rentalItem }, { headers });
  }

  jsonPatchRentalItem(
    rentalRequestId: string,
    rentalRequestPartitionKey: string,
    machineId: string,
    jsonPatchOperations: Operation[] | any[],
  ): Observable<any> {
    const headers = this.getAuth();
    return this.httpClient.patch<any>(
      `${environment.ordersApiDomain}/api/orders/v1/rental/${rentalRequestId}/item/${machineId}?partitionKey=${rentalRequestPartitionKey}`,
      jsonPatchOperations,
      { headers },
    );
  }

  setupChargesFromCartItem(cartItem: CartItem) {
    let dailySum = 0;
    let weeklySum = 0;
    let monthlySum = 0;

    let dailyDropdownItems = [];
    let weeklyDropdownItems = [];
    let monthlyDropdownItems = [];

    if (cartItem) {
      Object.keys(cartItem.dailySummary).forEach(key => {
        dailySum += cartItem.dailySummary[key];
        weeklySum += cartItem.weeklySummary[key];
        monthlySum += cartItem.monthlySummary[key];

        dailyDropdownItems.push({
          leftText: key,
          rightText: this.currencyPipe.transform(cartItem.dailySummary[key], "USD"),
        });

        weeklyDropdownItems.push({
          leftText: key,
          rightText: this.currencyPipe.transform(cartItem.weeklySummary[key], "USD"),
        });

        monthlyDropdownItems.push({
          leftText: key,
          rightText: this.currencyPipe.transform(cartItem.monthlySummary[key], "USD"),
        });
      });
    } else {
      console.warn(
        "Cart item does not exist, this means that the machine was added to the order after the order was placed. we need to either update cart so we can generate charges OR just generate charges better",
      );
    }

    const daily = [
      {
        header: this.currencyPipe.transform(dailySum, "USD"),
        toggled: false,
        dropdownItems: dailyDropdownItems,
      },
    ];

    const weekly = [
      {
        header: this.currencyPipe.transform(weeklySum, "USD"),
        toggled: false,
        dropdownItems: weeklyDropdownItems,
      },
    ];

    const monthly = [
      {
        header: this.currencyPipe.transform(monthlySum, "USD"),
        toggled: false,
        dropdownItems: monthlyDropdownItems,
      },
    ];

    return {
      daily,
      weekly,
      monthly,
    };
  }

  getAllRentalRequestItemData(): Observable<RentalRequestItem[]> {
    const searchRequest = new AzureSearchRequest();
    searchRequest.top = 1000;
    searchRequest.filter = `status ne 'Canceled' and status ne 'Complete'`;

    return this.azureSearchService.search<RentalRequest>(searchRequest, "rental-requests").pipe(
      mergeMap(result => {
        const rentalRequestIds = result.value.map(d => d.id);

        return forkJoin({
          rentalRequests: of(result.value),
          utilization: this.telematicsService.getUtilizationForRentalRequests(rentalRequestIds),
          gpsInformation: this.telematicsService.getGpsDataForRentalRequests(rentalRequestIds),
        });
      }),
      map(results => {
        let items = [];
        for (let rentalRequest of results.rentalRequests) {
          for (let item of rentalRequest.items.filter(d => d.rentalStage != "Cancelled" && d.rentalStage != "Confirmation")) {
            item.rentalRequest = rentalRequest;
            item.latestGpsInformation = results.gpsInformation.find(d => d.inventoryItemId == item.inventoryItem.id);
            item.gpsIcons = this.telematicsService.getGpsInformationIcons(item.latestGpsInformation);

            item.utilizations = results.utilization.filter(d => d.inventoryItemId == item.inventoryItem.id);
            items.push(item);
          }
        }

        return [
          ...items.filter(d => d.rentalStage == "Fulfillment"),
          ...items.filter(d => d.rentalStage == "OnRent"),
          ...items.filter(d => d.rentalStage == "ReturnFulfillment"),
        ] as RentalRequestItem[];
      }),
    );
  }
}
1;
